import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useGetCurrentUserFilters } from '../services/react-query/users/useGetUserFilters';
import { Filter } from '../types/Filter';
import { UserConfigurableModule } from '../types/User';

export enum DashboardFilterType {
  DEFAULT = 'default',
  SAVED = 'saved',
}

interface Options {
  module: UserConfigurableModule;
  defaultFilters: Filter[];
  savedSpan?: string | null;
}

export function useDashboardFilters({ module, defaultFilters, savedSpan }: Options) {
  const { data: rawSavedFilters = [], isLoading: isUserFiltersLoading } = useGetCurrentUserFilters({ page: module });
  const defaultUuids = _.map(defaultFilters, 'uuid');

  // Only filters with valid names
  const savedFilters = useMemo(() => _.filter(rawSavedFilters, (f) => !!f.name), [rawSavedFilters]);

  const allFilters = useMemo(() => [...defaultFilters, ...savedFilters], [defaultFilters, savedFilters]);

  const [selectedFilter, setSelectedFilter] = useState<string>();
  const filterType = _.includes(defaultUuids, selectedFilter) ? DashboardFilterType.DEFAULT : DashboardFilterType.SAVED;

  useEffect(() => {
    if (isUserFiltersLoading) return;
    if (!allFilters.length) return;

    const exists = _.find(allFilters, { uuid: savedSpan });
    const fallback = defaultUuids[0];

    const valid = exists ? savedSpan! : fallback;

    setSelectedFilter(valid);
  }, [isUserFiltersLoading, savedSpan, allFilters, defaultUuids]);

  // Build final API filters
  const filtersParam = useMemo(() => {
    if (!selectedFilter) return {};

    const list = filterType === DashboardFilterType.DEFAULT ? defaultFilters : savedFilters;

    return _.find(list, { uuid: selectedFilter })?.content || {};
  }, [selectedFilter, filterType, defaultFilters, savedFilters]);

  return {
    isUserFiltersLoading,
    savedFilters,
    selectedFilter,
    setSelectedFilter,
    filterType,
    filtersParam,
  };
}
